import React, { useMemo } from "react";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Grid from "components/common/Grid";
import { usePhcTheme } from "../PhcThemeContext";
import {
  normalizePerformanceAnalytics,
} from "../performanceAnalyticsData";

const MetricCard = ({ label, value, subLabel, tone }) => {
  const { theme } = usePhcTheme();

  return (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      height: "100%",
      minHeight: { xs: "auto", md: 138 },
      borderRadius: "20px",
      border: `1px solid ${theme.sectionBorderSoft || theme.sectionBorder}`,
      backgroundImage: theme.sectionBg,
      backgroundColor: theme.cardBg,
      boxShadow: theme.cardShadow,
      overflow: "hidden",
      p: "24px",
      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        pointerEvents: "none",
        boxShadow: theme.insetGlow,
      },
    }}
  >
    <Stack
      spacing="14px"
      sx={{ position: "relative", zIndex: 1, justifyContent: "center" }}
    >
      <Text
        sx={{
          fontWeight: 500,
          fontSize: { xs: 10, md: 12 },
          fontWeight: 800,
          color: theme.textPrimary,
          whiteSpace: "normal",
          // lineHeight: 1.25,
          minHeight: { xs: "auto", md: 10 },
          overflowWrap: "anywhere",
        }}
      >
        {label}
      </Text>
      <Text
        sx={{
          fontWeight: 700,
          fontSize: { xs: 18, md: 22 },
          color: (theme.perfTones && theme.perfTones[tone]) || theme.textPrimary,
          whiteSpace: "nowrap",
          lineHeight: "normal",
        }}
      >
        {value ?? "—"}
      </Text>
      <Text
        sx={{
          fontWeight: 400,
          fontSize: { xs: 10, md: 12 },
          fontWeight: 800,
          color: theme.textPrimary,
          lineHeight: "normal",
        }}
      >
        {subLabel || "—"}
      </Text>
    </Stack>
  </Box>
  );
};

/**
 * Performance Analytics — 
 * 6 metric cards: CAGR, Absolute Return, Alpha, Best/Worst Month, Win Rate
 */
const PerformanceAnalytics = ({ data }) => {
  const { theme } = usePhcTheme();
  const cards = useMemo(() => normalizePerformanceAnalytics(data), [data]);

  if (!cards.length) return null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "20px",
        border: `1px solid ${theme.sectionBorder}`,
        backgroundImage: theme.sectionBg,
        backgroundColor: theme.cardBg,
        boxShadow: theme.cardShadow,
        overflow: "hidden",
        px: { xs: 2, md: "19px" },
        py: { xs: 2.5, md: "24px" },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ position: "relative", zIndex: 1, mb: "24px" }}
      >
        <Text
          sx={{
            fontWeight: 600,
            fontSize: { xs: 15, md: 20 },
            color: theme.textPrimary,
            lineHeight: "normal",
            whiteSpace: "nowrap",
          }}
        >
          Performance Analytics
        </Text>
        <Text
          sx={{
            fontWeight: 400,
            fontSize: { xs: 13, md: 16 },
            color: theme.textMuted,
            lineHeight: "normal",
            whiteSpace: { xs: "normal", md: "nowrap" },
            textAlign: { xs: "left", md: "right" },
          }}
        >
          All figures are pre-tax.
        </Text>
      </Stack>

      {/* Divider */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          height: "1px",
          width: "100%",
          bgcolor: theme.sectionBorder,
          mb: "24px",
        }}
      />

      {/* Metric cards */}
      <Grid
        container
        spacing={{ xs: 1.5, md: "18px" }}
        sx={{ position: "relative", zIndex: 1 }}
      >
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={card.key}>
            <MetricCard
              label={card.label}
              value={card.value}
              subLabel={card.subLabel}
              tone={card.tone}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PerformanceAnalytics;
